import React from "react";
import { ArrowDownOutlined, ArrowRightOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { AnimatedNumber, Tooltip, Typography } from "../../../../../../../../shared/frontend/ui";
import type { I18nContextValue } from "../../../../app/i18n/useI18n";
import { formatDateOnly, formatMoney, formatNumber, formatPercent } from "../../../../app/units/format";
import { OverviewKpi } from "./OverviewKpi";
import { calculateAttendanceHeatmap, calculateAttendanceTrendPercent, DAYS_PER_MONTH_TO_WEEK_FACTOR } from "../personCheckInData";
import type { UnitsSettings } from "../../../../app/units/units";
import { SummaryListContent } from "../../../../components/SummaryListContent";
import { DashboardListDate } from "../../../dashboard/DashboardListDate";
import type { OpenItemRow } from "../personDetailTypes";
import type { PersonDigitalFileTimelineRow } from "../personDigitalFileTimelineUtils";

/** Zeichenbereich des Monats (MM) in einem ISO-Datum YYYY-MM-DD. */
const ISO_MONTH_START = 5;
const ISO_MONTH_END = 7;

type Text = I18nContextValue["text"];

/** Inhaltsspalte einer Listenzeile wie im Dashboard: eine Zeile, Überlauf mit Auslassung. */
const LIST_CONTENT_STYLE: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "block",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
};

/** Stufen der Wochenrasterfelder: 0 Besuche, 1, 2, ab 3. */
const HEATMAP_MAX_LEVEL = 3;
/** Hält die Monatszeile auch in Wochen ohne Monatsnamen gleich hoch, damit die Spalten bündig bleiben. */
const NBSP = "\u00a0";

const fill = (template: string, ...values: string[]) =>
  values.reduce((result, value, index) => result.replace(`{${index}}`, value), template);

/**
 * Block „Anwesenheit“: Kennzahlen wie im Lagebild (Besuche der letzten 30 Tage mit Trend gegenüber den 30 Tagen
 * davor, Wochenschnitt, Check-ins gesamt), darunter das Wochenraster der letzten 13 Wochen — ein Feld je Tag, je dunkler,
 * desto mehr Besuche. Einzelwerte und Daten stehen im Tooltip, nicht als Text.
 */
export function PersonAttendanceTrend({ sessions, stats, loading, text, units }: {
  sessions: Array<{ date: string }>;
  stats: { total: number; last7Days: number; last30Days: number; avgDuration7: number; avgDuration30: number };
  loading: boolean;
  text: Text;
  units: UnitsSettings;
}) {
  const weeks = React.useMemo(() => calculateAttendanceHeatmap(sessions), [sessions]);
  const trend = React.useMemo(() => calculateAttendanceTrendPercent(sessions), [sessions]);
  const perWeek = stats.last30Days / DAYS_PER_MONTH_TO_WEEK_FACTOR;
  const trendTone = trend == null || trend === 0 ? "neutral" : trend > 0 ? "up" : "down";
  const monthOf = (date: string) => formatDateOnly(date, units, { day: undefined, year: undefined, month: "short" });
  const isMonthStart = (weekIndex: number) =>
    weekIndex === 0 || weeks[weekIndex]![0]!.date.slice(ISO_MONTH_START, ISO_MONTH_END) !== weeks[weekIndex - 1]![0]!.date.slice(ISO_MONTH_START, ISO_MONTH_END);
  const trendSub = !loading && trend != null ? (
    <Tooltip title={fill(text("Person overview trend tooltip"), formatPercent(trend, units))}>
      <span className="person-attendance-trend" data-tone={trendTone}>
        {trend > 0 ? <ArrowUpOutlined /> : trend < 0 ? <ArrowDownOutlined /> : <ArrowRightOutlined />}
        {formatPercent(Math.abs(trend), units)}
      </span>
    </Tooltip>
  ) : undefined;
  return (
    <div className="person-attendance">
      <div className="person-overview-kpis">
        <OverviewKpi label={text("Person overview visits 30 days")} sub={trendSub}>
          {loading ? "–" : <AnimatedNumber value={stats.last30Days} format={(value) => formatNumber(Math.round(value), units)} />}
        </OverviewKpi>
        <OverviewKpi label={text("Person overview per week")}>
          {loading ? "–" : formatNumber(perWeek, units, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
        </OverviewKpi>
        <OverviewKpi label={text("Person overview check-ins total")}>
          {loading ? "–" : formatNumber(stats.total, units)}
        </OverviewKpi>
      </div>
      {/* Immer als Raster — auch ohne Besuche ist das leere Raster die Aussage. */}
      <div className="person-attendance-heatmap" role="img" aria-label={text("Person overview heatmap label")} data-testid="attendance-heatmap">
        {weeks.map((week, weekIndex) => (
          <div key={week[0]!.date} className="person-attendance-week">
            <span className="person-overview-kpi-label person-attendance-month" aria-hidden="true">{isMonthStart(weekIndex) ? monthOf(week[0]!.date) : NBSP}</span>
            {week.map((day) => (
              <Tooltip
                key={day.date}
                title={day.future ? undefined : fill(text("Person overview heatmap cell"), formatDateOnly(day.date, units), formatNumber(day.count, units))}
              >
                <span
                  className="person-attendance-day"
                  data-level={day.future ? undefined : Math.min(day.count, HEATMAP_MAX_LEVEL)}
                  data-future={day.future || undefined}
                />
              </Tooltip>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Offene Posten: Fälligkeit, offener Betrag, Bezeichnung. */
export function PersonOpenItemsList({ items, limit, error, text, units }: {
  items: OpenItemRow[];
  limit: number;
  error: boolean;
  text: Text;
  units: UnitsSettings;
}) {
  return (
    <SummaryListContent
      items={error ? [] : items}
      maxItems={limit}
      emptyText={error ? <Typography.Text type="danger">{text("Error loading data")}</Typography.Text> : text("Person overview no open items")}
      getItemKey={(item) => item.id}
      renderItem={(item) => (
        <>
          <DashboardListDate value={String(item.dueDate || "").slice(0, 10)} units={units} />
          <span style={LIST_CONTENT_STYLE}>
            <Typography.Text strong>{formatMoney(Number(item.openAmount ?? item.amount ?? 0), units)}</Typography.Text>
            {item.description ? <Typography.Text type="secondary"> · {item.description}</Typography.Text> : null}
          </span>
        </>
      )}
    />
  );
}

const TIMELINE_TYPE_KEYS: Record<PersonDigitalFileTimelineRow["type"], Parameters<Text>[0]> = {
  letter: "Letter",
  email: "Email",
  reminder: "Reminder",
  contract: "Contract",
  upload: "File",
  other: "Other"
};

/** Letzte Vorgänge der Akte (Dokumente, E-Mails, Mahnungen). */
export function PersonTimelineList({ rows, error, text, units }: {
  rows: PersonDigitalFileTimelineRow[];
  error: boolean;
  text: Text;
  units: UnitsSettings;
}) {
  return (
    <SummaryListContent
      items={error ? [] : rows}
      emptyText={error ? <Typography.Text type="danger">{text("Error loading data")}</Typography.Text> : text("Person overview no timeline entries")}
      getItemKey={(row) => row.id}
      renderItem={(row) => (
        <>
          <DashboardListDate value={row.occurredAt} units={units} />
          <span style={LIST_CONTENT_STYLE}>
            <Typography.Text strong>{text(TIMELINE_TYPE_KEYS[row.type] ?? "Other")}</Typography.Text>
            {row.title || row.summary ? <Typography.Text type="secondary"> · {row.title || row.summary}</Typography.Text> : null}
          </span>
        </>
      )}
    />
  );
}
